/**
 * Asystent-żarówka (spec 01 §0.3, spec 03 §10) — jeden komponent dla aplikacji
 * HR i portalu.
 *
 * Podpowiedzi liczy serwer z realnych danych i oddaje jako klucze i18n, więc
 * ten plik nie zna żadnej reguły biznesowej — tłumaczy i nawiguje. Gdy widok
 * nie ma faktów, FAB w ogóle się nie pokazuje: pusty panel udawałby, że system
 * coś zauważył [D26].
 */
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { assistantViewFor, canOpenAppPath, type AssistantHint } from '@k1p4/shared'
import { ChevronRight, Lightbulb, X, cx } from '@k1p4/ui'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { useFormat } from '../format'
import styles from './Assistant.module.css'

export interface AssistantProps {
  scope: 'app' | 'portal'
  /** Klucz `handle.meta` bieżącej trasy — decyduje, o co pytamy serwer. */
  metaKey: string
}

export function Assistant({ scope, metaKey }: AssistantProps) {
  const { t } = useTranslation()
  const fmt = useFormat()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const roles = useAuth().session?.user.roles ?? []

  const view = assistantViewFor(scope, metaKey)
  const hints = useQuery({
    queryKey: ['assistant', view],
    queryFn: () => api.assistantHints(view!),
    enabled: view !== null,
  })

  const items: AssistantHint[] = hints.data?.hints ?? []
  if (items.length === 0) return null

  return (
    <>
      {open ? (
        <aside className={styles.panel} aria-label={t('assistant.title')}>
          <header className={styles.header}>
            <span className={styles.headerIcon} aria-hidden="true">
              <Lightbulb size={18} />
            </span>
            <span className={styles.headerText}>
              <span className={styles.headerTitle}>{t('assistant.title')}</span>
              <span className={styles.headerSubtitle}>
                {t('assistant.subtitle', { count: items.length })}
              </span>
            </span>
            <button
              type="button"
              className={styles.close}
              aria-label={t('assistant.close')}
              onClick={() => setOpen(false)}
            >
              <X size={16} />
            </button>
          </header>

          <div className={styles.hints}>
            {items.map((hint) => (
              <div key={`${hint.key}-${hint.month ?? hint.meta ?? ''}`} className={styles.hint}>
                <span className={cx(styles.dot, styles[hint.tone])} aria-hidden="true" />
                <div>
                  <p className={styles.hintText}>
                    {t(`assistant.hints.${hint.key}`, {
                      count: hint.count,
                      /* Miesiąc z API formatujemy w języku użytkownika (fala 9, grupa 151). */
                      meta: hint.month ? fmt.month(hint.month, 'period') : (hint.meta ?? ''),
                    })}
                  </p>
                  {/* Cel poza zasięgiem roli nie jest przyciskiem — API i tak go odfiltrowuje (grupa 160). */}
                  {!hint.to.startsWith('/app/') || canOpenAppPath(roles, hint.to) ? (
                    <button
                      type="button"
                      className={styles.hintCta}
                      onClick={() => {
                        setOpen(false)
                        navigate(hint.to)
                      }}
                    >
                      {t(`assistant.hints.${hint.key}Cta`)}
                      <ChevronRight size={14} />
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          <p className={styles.footer}>{t('assistant.footer')}</p>
        </aside>
      ) : null}

      <button
        type="button"
        className={styles.fab}
        aria-label={open ? t('assistant.close') : t('assistant.open')}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Lightbulb size={22} />
        <span className={styles.fabBadge}>{items.length}</span>
      </button>
    </>
  )
}
